import { useState, useMemo, useCallback } from "react";

import { UNIQUE_PLAYERS } from '../data/players';
import { getFlexPositions, getSflexPositions } from '../engine/simulation';
import { getAdpRank, getAdpValue, getEffectivePosRank, parseRankNum } from '../data/adpData';
import { getActual2025PPG } from '../engine/projections';
import { filterPlayerList } from '../utils/filterPlayerList';

// Note: takes activeLeagueName as a fourth arg (needed by dumpPriceDiagnostic label)
export default function useBuilderState(settings, pricing, rosterSlots, activeLeagueName = null) {
  const { getPlayerValue, getPlayerProj, getPlayerGames, getPlayerVorp } = pricing;

  // ── State ─────────────────────────────────────────────────────────────────
  const [roster, setRoster] = useState({});
  const [lockedSlots, setLockedSlots] = useState(new Set());
  const [search, setSearch] = useState("");
  const [posFilter, setPosFilter] = useState(new Set(["ALL"]));
  const [sortBy, setSortBy] = useState("rank");
  const [mobileTab, setMobileTab] = useState("roster");
  const [showBuilderMenu, setShowBuilderMenu] = useState(false);
  const [assigningPlayer, setAssigningPlayer] = useState(null);
  const [targetSlotId, setTargetSlotId] = useState(null);
  const [showBuildPanel, setShowBuildPanel] = useState(false);
  const [selectedArchetype, setSelectedArchetype] = useState(null);
  const [buildModifiers, setBuildModifiers] = useState([]);
  const [buildStep, setBuildStep] = useState(0);
  const [buildName, setBuildName] = useState("");
  const [showSaved, setShowSaved] = useState(false);
  const [showSaveBuildDialog, setShowSaveBuildDialog] = useState(false);
  const [savedBuilds, setSavedBuilds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("auctionArchitect_savedBuilds")) || []; }
    catch { return []; }
  });

  const FLEX_POSITIONS = useMemo(() => getFlexPositions(settings.positions), [settings.positions]);
  const SFLEX_POSITIONS = useMemo(() => getSflexPositions(), []);
  const BUDGET = settings.budget;

  // ── Computed ──────────────────────────────────────────────────────────────
  const rosterHasPlayers = Object.keys(roster).length > 0;

  const rosteredPlayerNames = useMemo(() => {
    return new Set(Object.values(roster).map(p => p.name));
  }, [roster]);

  const spent = useMemo(() => {
    return Object.values(roster).reduce((sum, p) => sum + getPlayerValue(p), 0);
  }, [roster, getPlayerValue]);

  const emptySlots = rosterSlots.length - Object.keys(roster).length;
  const maxBid = emptySlots > 1 ? BUDGET - spent - (emptySlots - 1) : BUDGET - spent;
  const remaining = BUDGET - spent;

  const projectedTotal = useMemo(() => {
    return Object.values(roster).reduce((sum, p) => sum + getPlayerProj(p), 0);
  }, [roster, getPlayerProj]);

  const starterProjectedTotal = useMemo(() => {
    return Object.entries(roster).reduce((sum, [slotId, p]) => {
      const slot = rosterSlots.find(s => s.id === slotId);
      if (slot && slot.pos !== "BN") return sum + getPlayerProj(p);
      return sum;
    }, 0);
  }, [roster, rosterSlots, getPlayerProj]);

  /**
   * 🔴 SUMMED PER PLAYER, because the divisor is per player. This was
   * starterProjectedTotal / 17 — one constant over a total — and a projection is
   * normalised to ITS OWN games (~15 in the house set, to price in average games
   * missed; 17 for DEF/K, whose line is extrapolated up before it is scored).
   * A season total has no single divisor once the starters disagree about what a
   * season is, so the rate has to be built a player at a time and added up. Same
   * definition Draft Plan uses — see ppgTotals.
   */
  const week1Projection = useMemo(() => {
    if (starterProjectedTotal <= 0) return 0;
    const total = Object.entries(roster).reduce((sum, [slotId, p]) => {
      const slot = rosterSlots.find(s => s.id === slotId);
      if (!slot || slot.pos === "BN") return sum;
      const games = Number(getPlayerGames ? getPlayerGames(p) : 0);
      return sum + getPlayerProj(p) / (Number.isFinite(games) && games > 0 ? games : 17);
    }, 0);
    return total.toFixed(2);
  }, [roster, rosterSlots, starterProjectedTotal, getPlayerProj, getPlayerGames]);

  const spendingByPos = useMemo(() => {
    const breakdown = {};
    const slotMap = {};
    rosterSlots.forEach(s => { slotMap[s.id] = s.pos; });
    Object.entries(roster).forEach(([slotId, p]) => {
      const slotPos = slotMap[slotId] || p.pos;
      const pos = (slotPos === "BN" || slotPos === "FLEX" || slotPos === "SFLEX") ? slotPos : p.pos;
      if (!breakdown[pos]) breakdown[pos] = 0;
      breakdown[pos] += getPlayerValue(p);
    });
    return breakdown;
  }, [roster, getPlayerValue, rosterSlots]);

  const togglePosFilter = useCallback((pos) => {
    setPosFilter(prev => {
      const next = new Set(prev);
      if (pos === "ALL") return new Set(["ALL"]);
      next.delete("ALL");
      if (pos === "FLEX") {
        const flexSet = new Set(["RB", "WR", "TE"]);
        const allFlexActive = ["RB", "WR", "TE"].every(p => next.has(p));
        if (allFlexActive) flexSet.forEach(p => next.delete(p));
        else flexSet.forEach(p => next.add(p));
      } else if (pos === "SFLEX") {
        const sflexSet = new Set(["QB", "RB", "WR", "TE"]);
        const allSflexActive = ["QB", "RB", "WR", "TE"].every(p => next.has(p));
        if (allSflexActive) sflexSet.forEach(p => next.delete(p));
        else sflexSet.forEach(p => next.add(p));
      } else {
        if (next.has(pos)) next.delete(pos);
        else next.add(pos);
      }
      if (next.size === 0) return new Set(["ALL"]);
      return next;
    });
  }, []);

  const isFilterActive = useCallback((pos) => {
    if (pos === "ALL") return posFilter.has("ALL");
    if (pos === "FLEX") return ["RB", "WR", "TE"].every(p => posFilter.has(p)) && !posFilter.has("ALL");
    if (pos === "SFLEX") return ["QB", "RB", "WR", "TE"].every(p => posFilter.has(p)) && !posFilter.has("ALL");
    return posFilter.has(pos) && !posFilter.has("ALL");
  }, [posFilter]);

  const filteredPlayers = useMemo(() => {
    const available = UNIQUE_PLAYERS.filter(p => !rosteredPlayerNames.has(p.name));
    let list = filterPlayerList(available, posFilter, search);
    list.sort((a, b) => {
      if (sortBy === "value") return getPlayerValue(b) - getPlayerValue(a);
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "proj") return getPlayerProj(b) - getPlayerProj(a);
      if (sortBy === "vorp") return getPlayerVorp(b) - getPlayerVorp(a);
      if (sortBy === "ppg") {
        const aPPG = getActual2025PPG(a.name, a.pos, settings.scoring);
        const bPPG = getActual2025PPG(b.name, b.pos, settings.scoring);
        if (aPPG == null && bPPG == null) return 0;
        if (aPPG == null) return 1;
        if (bPPG == null) return -1;
        return bPPG - aPPG;
      }
      if (sortBy === "rank") {
        const singlePos = posFilter.size === 1 && !posFilter.has("ALL");
        if (singlePos) return parseRankNum(getEffectivePosRank(a.name, a.posRank)) - parseRankNum(getEffectivePosRank(b.name, b.posRank));
        return (getAdpRank(a.name) || 999) - (getAdpRank(b.name) || 999);
      }
      if (sortBy === "adp") return parseFloat(getAdpValue(a.name) || "999") - parseFloat(getAdpValue(b.name) || "999");
      if (sortBy === "tier") return (a.tier || 99) - (b.tier || 99);
      return 0;
    });
    return list;
  }, [posFilter, search, sortBy, rosteredPlayerNames, getPlayerValue, getPlayerProj, getPlayerVorp, settings.scoring]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const getAvailableSlots = useCallback((player) => {
    return rosterSlots.filter(slot => {
      if (roster[slot.id]) return false;
      if (slot.pos === "BN") return true;
      if (slot.pos === "FLEX") return FLEX_POSITIONS.includes(player.pos);
      if (slot.pos === "SFLEX") return SFLEX_POSITIONS.includes(player.pos);
      return slot.pos === player.pos;
    });
  }, [rosterSlots, roster, FLEX_POSITIONS, SFLEX_POSITIONS]);

  const addPlayer = useCallback((player, slotId) => {
    setRoster(prev => ({ ...prev, [slotId]: player }));
    setAssigningPlayer(null);
    setTargetSlotId(null);
    setMobileTab("roster");
  }, []);

  const handlePlayerClick = useCallback((player) => {
    const slots = getAvailableSlots(player);
    if (slots.length === 0) return;
    const val = getPlayerValue(player);
    if (val > maxBid && emptySlots > 0) return;
    if (targetSlotId && slots.find(s => s.id === targetSlotId)) {
      addPlayer(player, targetSlotId);
    } else if (slots.length === 1) {
      addPlayer(player, slots[0].id);
    } else {
      setAssigningPlayer(player);
    }
  }, [getAvailableSlots, getPlayerValue, maxBid, emptySlots, targetSlotId, addPlayer]);

  const removePlayer = useCallback((slotId) => {
    setRoster(prev => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
    setLockedSlots(prev => { const next = new Set(prev); next.delete(slotId); return next; });
  }, []);

  const toggleLock = useCallback((slotId) => {
    setLockedSlots(prev => {
      const next = new Set(prev);
      if (next.has(slotId)) next.delete(slotId);
      else next.add(slotId);
      return next;
    });
  }, []);

  const saveBuild = useCallback(() => {
    if (!rosterHasPlayers || !buildName.trim()) return;
    const snapshotRoster = {};
    let snapshotSpent = 0;
    for (const [slotId, player] of Object.entries(roster)) {
      const price = getPlayerValue(player);
      snapshotRoster[slotId] = { ...player, savedPrice: price };
      snapshotSpent += price;
    }
    const build = {
      id: Date.now(), name: buildName.trim(), roster: snapshotRoster,
      league: activeLeagueName || null,
      settings: { scoring: { ...settings.scoring }, positions: { ...settings.positions }, budget: settings.budget, teams: settings.teams },
      spent: snapshotSpent, projectedTotal, timestamp: Date.now(),
    };
    const updated = [...savedBuilds, build];
    setSavedBuilds(updated);
    localStorage.setItem("auctionArchitect_savedBuilds", JSON.stringify(updated));
    setBuildName("");
    setShowSaveBuildDialog(false);
  }, [rosterHasPlayers, buildName, roster, getPlayerValue, settings, projectedTotal, savedBuilds, activeLeagueName]);

  const loadBuild = useCallback((build) => {
    setRoster({ ...build.roster });
    setShowSaved(false);
  }, []);

  const deleteBuild = useCallback((id) => {
    const updated = savedBuilds.filter(b => (b.id || b.timestamp) !== id);
    setSavedBuilds(updated);
    localStorage.setItem("auctionArchitect_savedBuilds", JSON.stringify(updated));
  }, [savedBuilds]);

  const clearRoster = useCallback(() => {
    setRoster(prev => {
      if (lockedSlots.size === 0) return {};
      const next = {};
      for (const [id, player] of Object.entries(prev)) {
        if (lockedSlots.has(id)) next[id] = player;
      }
      return next;
    });
  }, [lockedSlots]);

  return {
    // State
    roster, setRoster,
    lockedSlots, setLockedSlots,
    search, setSearch,
    posFilter, setPosFilter,
    sortBy, setSortBy,
    mobileTab, setMobileTab,
    showBuilderMenu, setShowBuilderMenu,
    assigningPlayer, setAssigningPlayer,
    targetSlotId, setTargetSlotId,
    showBuildPanel, setShowBuildPanel,
    selectedArchetype, setSelectedArchetype,
    buildModifiers, setBuildModifiers,
    buildStep, setBuildStep,
    buildName, setBuildName,
    showSaved, setShowSaved,
    showSaveBuildDialog, setShowSaveBuildDialog,
    savedBuilds, setSavedBuilds,
    // Slot geometry
    FLEX_POSITIONS, SFLEX_POSITIONS, BUDGET,
    // Computed
    rosterHasPlayers,
    rosteredPlayerNames,
    spent, emptySlots, maxBid, remaining,
    projectedTotal, starterProjectedTotal, week1Projection,
    spendingByPos,
    filteredPlayers,
    // Actions
    getAvailableSlots,
    togglePosFilter, isFilterActive,
    addPlayer, handlePlayerClick,
    removePlayer, toggleLock,
    saveBuild, loadBuild, deleteBuild,
    clearRoster,
  };
}
